// import { UUID } from 'uuid';
import { InsightPattern } from '../features/insights/types';
import store from './store';
export type UUID = string;
export type AppDispatch = typeof store.dispatch;

export interface QuestionId {
  questionId: number;
  questionText: string;
}

export interface ReframedThought {
  thought: string;
  created_at: string;
  uuid: UUID;
}

export interface Classifications {
  sentiment: string[];
  emotions: string[];
  topic: string[];
  wellbeing: string[];
}

export interface Thought {
  thought: string;
  uuid: string;
  created_at: string;
  updated_at: string;
  sentiment: string;
  computed_top_emotions: string[];
  computed_topics: string[];
  user_computed_emotions?: string[];
  user_selected_emotions: string[];
  reframed: string;
  tags: string[];
  agent_response?: string;
  journal_prompt?: string | null;
  saged_thought_id?: string | null;
  classifications: Classifications;
  guiding_questions?: string[];
  answers_to_guiding_questions?: string[];
  guiding_question_parent_id?: string;
  _isPending?: boolean; // Indicates if this is a pending thought awaiting server response
  _pendingId?: string; // Reference to the original pending ID before resolution
  _error?: string; // Error message if thought submission failed
  _isStreaming?: boolean; // Indicates if streaming agent response is in progress
  _needsAgentResponse?: boolean; // Indicates if thought needs to start streaming agent response
  _responseParts?: import('../features/home/types/responseParts').ResponsePart[]; // Ordered segments of response (text + investigations)
  _investigations?: Record<string, import('../features/home/types/responseParts').Investigation>; // Investigation state by ID
}

export interface Bias {
  name: string;
  reason: string;
}

export interface GuidingQuestions {
  question: string;
  reasoning: string;
}
export type DateRange = 'Day' | 'Week' | 'Month';

export interface SageThought {
  user_reframed_thought: string;
  uuid: string;
  empathy: string;
  guiding_questions: string[];
  created_at: string;
  reframed_thought: string;
  healthy_suggestions: string;
  source_thought_id: string | null;
  answers_to_guiding_questions: string[];
}

export interface SageThoughts {
  [key: string]: SageThought;
}

export interface Thoughts {
  all_thoughts: Thought[];
}

export interface CosmosResponse {
  message: string;
  uuid: string;
  created_at: string;
  related_thought_id?: string | null;
}

export interface UserResponse {
  message: string;
  uuid: string;
  created_at: string;
}

export interface ChatState {
  cosmos_latest_response: CosmosResponse | null;
  users_latest_response: UserResponse | null;
}

export type FeedbackType = 'binary_preference' | 'rating_scale' | 'form' | 'survey';
export type BinaryPreferenceRating = 'thumbs_up' | 'thumbs_down';

export interface BinaryPreferenceValue {
  rating: BinaryPreferenceRating;
  comment?: string;
}

export interface FeedbackRequest {
  type: FeedbackType;
  target_type: string;
  target_id: string;
  value: BinaryPreferenceValue | Record<string, any>;
  metadata?: Record<string, any>;
}

export interface FeedbackResponse {
  feedback_id: string;
  success: boolean;
}

export interface MindmapNode {
  id: string;
  count: number;
  size: number;
  label: string;
}

export interface MindmapEdge {
  source: string;
  target: string;
  weight: number;
}

export interface MindmapAlgorithms {
  communities: string[][];
  centrality: Record<string, number>;
}

export interface MindmapData {
  nodes: MindmapNode[];
  edges: MindmapEdge[];
  algorithms: MindmapAlgorithms;
}

export interface MindmapState {
  data: MindmapData | null;
  loading: boolean;
  error: string | null;
}

// Badge state for rolling 30-day Active Badges system
export type BadgeState = 'active' | 'at_risk' | 'lost' | 'available';

// Weekly progress tracking interface
export interface WeeklyProgress {
  current: number;
  target: number;
  deadline: string; // e.g., "Sunday" or ISO date string
  actionText?: string; // e.g., "3 reflections needed by Sunday"
}

export interface Achievement {
  id: string;
  name: string;
  description: string;
  multiplier: number;
  tier: 'common' | 'rare' | 'epic' | 'legendary';
  category: 'consistency' | 'depth' | 'transformation' | 'mindfulness' | 'exploration' | 'volume';
  color: string;
  icon: string;
  unlocked: boolean;
  progress_current: number;
  progress_target: number;
  progress_label: string;
  recommendation?: string;

  // New fields for Active Badges system (rolling 30-day tracking)
  state?: BadgeState; // Current badge state
  weeklyProgress?: WeeklyProgress; // Weekly maintenance tracking
  lostDate?: string; // ISO date string when badge was lost
  daysHeld?: number; // Number of days actively held (for active badges)
}

export interface WellnessScoreData {
  overall_score: number;
  tier: string;
  period_days: number;
  generated_at: string;
  base_score: number;
  achievement_multiplier: number;
  achievements: Achievement[];
  unlockable_achievements: Achievement[];
}

export interface WellnessScoreState {
  data: WellnessScoreData | null;
  loading: boolean;
  error: string | null;
}

export interface WellnessCalendarDay {
  date: string; // ISO format YYYY-MM-DD
  score: number; // 0-100
  tier: 'Emerging' | 'Developing' | 'Strong' | 'Thriving';
}

export interface WellnessCalendarData {
  days: WellnessCalendarDay[];
  period_days: number;
  start_date: string;
  end_date: string;
}

export interface WellnessCalendarState {
  data: WellnessCalendarData | null;
  loading: boolean;
  error: string | null;
}

export interface InsightsState {
  patterns: InsightPattern[];
  loading: boolean;
  error: string | null;
}

export type NounCount = { noun: string; occurrences: number };
export type Nouns = {
  nouns: string[];
  noun_chunks: string[];
};

export type SentimentThoughtCounts = {
  positive: number;
  negative: number;
  neutral: number;
};

export interface Stats {
  sentimentScore: number;
  weeklyWellbeing: WellbeingCompletion[];
  weeklyFetched: boolean;
  monthlyFetched: boolean;
  monthlyWellbeing: WellbeingCompletion[];
  weeklyThoughtNouns: SentimentThoughtCounts | null;
  monthlyThoughtNouns: SentimentThoughtCounts | null;
  allTimeThoughtNouns: SentimentThoughtCounts | null;
  allTimeThoughtCounts: SentimentThoughtCounts | null;
  weeklyThoughtSentiment: {
    positive: number;
    negative: number;
    neutral: number;
  };
}

export interface User {
  // userId: number;
  firstName: string;
  // lastName: string;
  // email: string;
}

export interface Question {
  questionId: number;
  questionText: string;
}

export interface QuestionResponse {
  questionId: number;
  response: string;
  updateTimestamp: number;
}

export interface Response {
  // question: string;
  response: string;
  updateTimestamp: string;
}

export interface Checkin {
  checkinId: number;
  checkinType: 'morning' | 'evening';
  userId: number;
  timestamp: number; // Date.now()
  questionResponses: QuestionResponse[];
}

export interface Gratitude {
  id: number;
  response: string;
  updateTimestamp: string;
}

export interface Task {
  taskId: number;
  taskName: string;
}

export interface Intention {
  id: number;
  text: string;
  completed: boolean;
  editable: boolean; // for editing
  completedTimestamp: string | null;
  textUpdatedTimestamp: string | null;
}

export interface Priority {
  task: string;
  completed: boolean;
  completedTimestamp: string | null;
  taskUpdatedTimestamp: string;
}

export interface TaskResult {
  taskId: number;
  completed: boolean;
  updateTimestamp: number;
}

export interface WellbeingCompletion {
  mind: boolean;
  body: boolean;
  spirit: boolean;
}

export interface Spirit {
  setsCompleted: number;
  completed: boolean;
}

export interface Body {
  activities: string[];
  completed: boolean;
}

export interface Mind {
  gratitudes: string[];
  completed: boolean;
}

export interface List {
  id: number;
  name: string;
  tasks: Task[];
}

export type AuthorizedState = 'authorized' | 'user_not_found' | 'incorrect_pin' | 'unauthorized';

export interface Auth {
  sessionToken: string | null;
  authorized_state: AuthorizedState;
  pinToken: string | null;
  isAuthenticated: boolean | null;
  isAuthorized: boolean;
}

export interface Config {
  timerDuration: number;
}

export interface PulseOfPlanet {
  saged: number;
  joyful: number;
  breath_count: number;
  gratitudes: string[];
}

export interface ReportMetadata {
  id: string;
}

export interface OnboardingState {
  tasks: {
    pinSetup: boolean;
    firstLogin: boolean;
  };
  hasCompletedOnboarding: boolean;
  currentOnboardingStep: number | null;
  isOnboardingActive: boolean;
  testMode: boolean;
}

export interface ThoughtsState {
  all_thoughts: Thought[];
  loading: boolean;
}

export interface VaultLockState {
  isLocked: boolean;
}

export interface RootState {
  user: User;
  auth: Auth;
  config: Config;
  pulseOfPlanet: PulseOfPlanet;
  thoughts: ThoughtsState;
  sageThoughts: Record<string, SageThought>;
  stats: Stats;
  reportMetadata: ReportMetadata | null;
  onboarding: OnboardingState;
  vaultLock: VaultLockState;
  chat: ChatState;
  mindmap: MindmapState;
  wellnessScore: WellnessScoreState;
  wellnessCalendar: WellnessCalendarState;
  insights: InsightsState;
}

export const initialState: RootState = {
  user: {
    firstName: '',
  },
  config: {
    timerDuration: 20,
  },
  auth: {
    sessionToken: null,
    pinToken: null,
    authorized_state: 'unauthorized',
    isAuthenticated: null,
    isAuthorized: false,
  },
  pulseOfPlanet: {
    saged: 100,
    joyful: 100,
    breath_count: 0,
    gratitudes: [],
  },
  stats: {
    sentimentScore: 0,
    weeklyWellbeing: [],
    monthlyWellbeing: [],
    weeklyFetched: false,
    monthlyFetched: false,
    weeklyThoughtNouns: null,
    monthlyThoughtNouns: null,
    allTimeThoughtNouns: null,
    allTimeThoughtCounts: null,
    weeklyThoughtSentiment: {
      positive: 0,
      negative: 0,
      neutral: 0,
    },
  },
  thoughts: {
    all_thoughts: [],
    loading: false,
  },
  sageThoughts: {},
  reportMetadata: null,
  onboarding: {
    tasks: {
      pinSetup: false,
      firstLogin: false,
    },
    hasCompletedOnboarding: false,
    currentOnboardingStep: null,
    isOnboardingActive: false,
    testMode: false,
  },
  vaultLock: {
    isLocked: false,
  },
  chat: {
    cosmos_latest_response: null,
    users_latest_response: null,
  },
  mindmap: {
    data: null,
    loading: false,
    error: null,
  },
  wellnessScore: {
    data: null,
    loading: false,
    error: null,
  },
  wellnessCalendar: {
    data: null,
    loading: false,
    error: null,
  },
  insights: {
    patterns: [],
    loading: false,
    error: null,
  },
};
